/**
 * Pure helpers for the roster page's **date window** — the span of the
 * team's date line the roster page shows (#1950 / #1956, founder 2 Oct
 * 2026: "every date within the team should be its own immutable data
 * point … the left right button should just be showing in terms of each
 * week, or each 2 weeks, or however long they are setting the view for").
 *
 * The window is a view onto the team's dates, carried in the URL as
 * `?from=YYYY-MM-DD&to=YYYY-MM-DD&preset=…`: a week, a fortnight (the
 * default — this week and next), four weeks, a calendar month, or any
 * custom span up to the range read's cap (62 days). ◀ ▶ step it by its own
 * length and never leave the page. The internal solve windows that own the
 * dates are resolved per date by the page (`lib/rosterDateOwners.ts`);
 * nothing here knows about them except `cycleWindow` and its two callers,
 * which the old `/cycles/:id` redirect and the import helpers use.
 *
 * No React, no I/O. ISO date strings sort chronologically, so range
 * comparisons are plain string compares. Unit-tested in
 * `web/tests/rosterDatesWindow.test.ts`.
 */

import { type DateWindow, parseISO, toISO } from "./dateWindow";

export type { DateWindow };

/** Named window shapes (#1956: "Whole roster" is gone — a team's dates
 *  have no whole). */
export type RosterDatesPreset = "week" | "fortnight" | "4w" | "month" | "custom";

export type NamedRosterPreset = Exclude<RosterDatesPreset, "custom">;

export const ROSTER_PRESET_ORDER: ReadonlyArray<NamedRosterPreset> = [
  "week",
  "fortnight",
  "4w",
  "month",
];

export const ROSTER_PRESET_LABEL: Record<NamedRosterPreset, string> = {
  week: "Week",
  fortnight: "Fortnight",
  "4w": "4 weeks",
  month: "Month",
};

/**
 * The most days one window may span — the range read's cap
 * (`DEFAULT_RANGE_ROSTER_MAX_DAYS` in `backend/.../range_roster.py`). A
 * longer custom span is cut to this length and the bar says so.
 */
export const MAX_WINDOW_DAYS = 62;

/** The minimum an internal solve window needs for window math. Structurally
 *  satisfied by the `/api/teams/{id}/cycles` rows and `/api/cycles/{id}`. */
export interface CycleSpan {
  id: string;
  horizon_start: string;
  horizon_days: number;
  name?: string;
  state?: string;
}

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

export function addDaysIso(iso: string, days: number): string {
  const d = parseISO(iso);
  d.setDate(d.getDate() + days);
  return toISO(d);
}

/** Inclusive day count of a window (`from` and `to` both counted). */
export function windowDays(w: DateWindow): number {
  const a = parseISO(w.from).getTime();
  const b = parseISO(w.to).getTime();
  return Math.round((b - a) / 86_400_000) + 1;
}

/** The Monday on or before `iso`. */
export function mondayOf(iso: string): string {
  const d = parseISO(iso);
  const dow = (d.getDay() + 6) % 7; // Mon = 0 … Sun = 6
  d.setDate(d.getDate() - dow);
  return toISO(d);
}

function firstOfMonth(iso: string): string {
  const d = parseISO(iso);
  return toISO(new Date(d.getFullYear(), d.getMonth(), 1));
}

function lastOfMonth(iso: string): string {
  const d = parseISO(iso);
  return toISO(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

/**
 * `n` whole calendar months from the first of `anchorIso`'s month (#1909).
 *
 * The Manpower page's working-periods timeline needs a months-long window
 * — "the next three months" — where the roster's week-anchored presets are
 * the wrong unit. Lives here so both surfaces share one month arithmetic
 * (and one unit test file); the roster's own preset list is unchanged.
 */
export function monthsWindow(anchorIso: string, months: number): DateWindow {
  const d = parseISO(anchorIso);
  const n = Math.max(1, Math.round(months));
  const from = new Date(d.getFullYear(), d.getMonth(), 1);
  // Day 0 of the month after the last one included = its last day.
  const to = new Date(d.getFullYear(), d.getMonth() + n, 0);
  return { from: toISO(from), to: toISO(to) };
}

/** An internal solve window's whole horizon as an inclusive window. */
export function cycleWindow(cycle: Pick<CycleSpan, "horizon_start" | "horizon_days">): DateWindow {
  return {
    from: cycle.horizon_start,
    to: addDaysIso(cycle.horizon_start, Math.max(1, cycle.horizon_days) - 1),
  };
}

export function isValidWindow(w: DateWindow): boolean {
  return ISO_RE.test(w.from) && ISO_RE.test(w.to) && w.from <= w.to;
}

export function sameWindow(a: DateWindow, b: DateWindow): boolean {
  return a.from === b.from && a.to === b.to;
}

export function windowsOverlap(a: DateWindow, b: DateWindow): boolean {
  return a.from <= b.to && b.from <= a.to;
}

/** Intersection of two windows, or `null` when they share no day. */
export function intersectWindows(a: DateWindow, b: DateWindow): DateWindow | null {
  const from = a.from > b.from ? a.from : b.from;
  const to = a.to < b.to ? a.to : b.to;
  return from <= to ? { from, to } : null;
}

/** Every ISO day of `w`, in order. */
export function daysOf(w: DateWindow): string[] {
  const out: string[] = [];
  if (!isValidWindow(w)) return out;
  let cursor = w.from;
  // Bounded well past the cap so a malformed window never spins.
  for (let i = 0; i < 400 && cursor <= w.to; i += 1) {
    out.push(cursor);
    cursor = addDaysIso(cursor, 1);
  }
  return out;
}

/**
 * The window a preset means, anchored on `anchorIso` (the first day of the
 * window on screen, so switching Fortnight → Week keeps the lead where
 * they are; today on a fresh page).
 */
export function presetWindow(preset: NamedRosterPreset, anchorIso: string): DateWindow {
  switch (preset) {
    case "week": {
      const from = mondayOf(anchorIso);
      return { from, to: addDaysIso(from, 6) };
    }
    case "fortnight": {
      const from = mondayOf(anchorIso);
      return { from, to: addDaysIso(from, 13) };
    }
    case "4w": {
      const from = mondayOf(anchorIso);
      return { from, to: addDaysIso(from, 27) };
    }
    case "month":
      return { from: firstOfMonth(anchorIso), to: lastOfMonth(anchorIso) };
  }
}

/** Which preset a window's shape is. Custom when none fits. */
export function matchedRosterPreset(w: DateWindow): RosterDatesPreset {
  if (w.from === firstOfMonth(w.from) && w.to === lastOfMonth(w.from)) return "month";
  if (w.from === mondayOf(w.from)) {
    const n = windowDays(w);
    if (n === 7) return "week";
    if (n === 14) return "fortnight";
    if (n === 28) return "4w";
  }
  return "custom";
}

/**
 * A window no longer than `MAX_WINDOW_DAYS`: `w` itself, or `w` cut at its
 * start + 61 days with `capped: true` so the bar can say why.
 */
export function capWindow(w: DateWindow): { window: DateWindow; capped: boolean } {
  if (windowDays(w) <= MAX_WINDOW_DAYS) return { window: w, capped: false };
  return { window: { from: w.from, to: addDaysIso(w.from, MAX_WINDOW_DAYS - 1) }, capped: true };
}

/**
 * Step a window backwards (`-1`) or forwards (`+1`) by its own length. A
 * calendar month steps by month; a custom span steps by its day count.
 * This is the ONLY thing ◀ ▶ do (#1956): the page never changes.
 */
export function shiftWindow(w: DateWindow, direction: -1 | 1): DateWindow {
  if (w.from === firstOfMonth(w.from) && w.to === lastOfMonth(w.from)) {
    const d = parseISO(w.from);
    const first = new Date(d.getFullYear(), d.getMonth() + direction, 1);
    const from = toISO(first);
    return { from, to: lastOfMonth(from) };
  }
  const n = windowDays(w);
  return { from: addDaysIso(w.from, direction * n), to: addDaysIso(w.to, direction * n) };
}

/** `iso` when it is a real calendar day written `YYYY-MM-DD`, else `null`.
 *  `2026-02-30` is rejected (it would roll over to March). */
export function validIsoDay(iso: string | null | undefined): string | null {
  if (!iso || !ISO_RE.test(iso)) return null;
  return toISO(parseISO(iso)) === iso ? iso : null;
}

/**
 * KKH REQ-05 / REQ-09 — the window to show so that `iso` is on screen.
 *
 * `w` itself when it already contains the day; otherwise `w` stepped by its
 * own length (`shiftWindow`, so a fortnight stays a Monday-aligned
 * fortnight and a calendar month stays a month) until it does. The lead's
 * chosen window SHAPE survives a deep link to a day outside it; only its
 * position moves.
 */
export function windowIncluding(w: DateWindow, iso: string): DateWindow {
  if (!isValidWindow(w) || (iso >= w.from && iso <= w.to)) return w;
  const direction: -1 | 1 = iso < w.from ? -1 : 1;
  let next = w;
  // Bounded: a deep link is days-to-months away; 400 steps of even a
  // one-day window covers more than a year.
  for (let i = 0; i < 400; i += 1) {
    next = shiftWindow(next, direction);
    if (iso >= next.from && iso <= next.to) return next;
  }
  return w;
}

/**
 * **The default window: this week and next.**
 *
 * Founder, 28 Sep 2026: "the default view is two weeks, from the Monday of
 * the current week to the Sunday of the next week." #1956 — no longer
 * clipped to anything: the team's date line has no edges, and dates no one
 * has solved simply show empty.
 */
export function defaultRosterWindow(today: string = todayIso()): DateWindow {
  return presetWindow("fortnight", today);
}

/**
 * The window a roster URL means: `?from&to` when it is a valid range
 * (capped at `MAX_WINDOW_DAYS`), else the default fortnight.
 */
export function landingRosterWindow(search: string, today: string = todayIso()): DateWindow {
  const params = new URLSearchParams(search);
  const from = validIsoDay(params.get("from"));
  const to = validIsoDay(params.get("to"));
  if (from && to) {
    const w = { from, to };
    if (isValidWindow(w)) return capWindow(w).window;
  }
  return defaultRosterWindow(today);
}

/** True when the URL asked for more than `MAX_WINDOW_DAYS` days. */
export function searchAskedPastCap(search: string): boolean {
  const params = new URLSearchParams(search);
  const from = validIsoDay(params.get("from"));
  const to = validIsoDay(params.get("to"));
  return Boolean(from && to && from <= to && windowDays({ from, to }) > MAX_WINDOW_DAYS);
}

/**
 * The preset the bar marks: `custom` when the URL says so (a custom span
 * that happens to be a Monday fortnight stays "custom" for the lead who
 * typed it), else the window's own shape.
 */
export function presetFromSearch(search: string, w: DateWindow): RosterDatesPreset {
  const raw = new URLSearchParams(search).get("preset");
  if (raw === "custom") return "custom";
  return matchedRosterPreset(w);
}

/**
 * The search string that names a window. Writes `from`, `to` and `preset`
 * (the window's shape unless one is given); other params are kept.
 */
export function searchWithWindow(
  currentSearch: string,
  w: DateWindow,
  preset: RosterDatesPreset = matchedRosterPreset(w),
): string {
  const params = new URLSearchParams(currentSearch);
  params.set("from", w.from);
  params.set("to", w.to);
  params.set("preset", preset);
  const s = params.toString();
  return s ? `?${s}` : "";
}

/**
 * Where an old `/cycles/:id` link lands (#1956): that window's first two
 * weeks — `horizon_start` to `horizon_start + 13`, clipped to the window's
 * last day.
 */
export function redirectWindowForCycle(
  cycle: Pick<CycleSpan, "horizon_start" | "horizon_days">,
): DateWindow {
  const whole = cycleWindow(cycle);
  const to = addDaysIso(whole.from, 13);
  return { from: whole.from, to: to < whole.to ? to : whole.to };
}

/** Live (non-archived) internal windows that share at least one day with
 *  `w`, earliest first. Archived rows are history and never own dates. */
export function cyclesTouching<C extends CycleSpan & { phase?: string }>(
  w: DateWindow,
  cycles: readonly C[],
): C[] {
  return cycles
    .filter((c) => c.phase !== "archived")
    .filter((c) => windowsOverlap(w, cycleWindow(c)))
    .sort((a, b) => a.horizon_start.localeCompare(b.horizon_start));
}

/**
 * The first day of `w` a live internal window covers, with that window —
 * `null` when none does (truth pass, 1 Oct 2026: an import's apply outcome
 * opens the roster on it). The earliest-starting window touching `w` holds
 * it: one that starts on or before `w.from` covers `w.from`, else its own
 * first day is the earliest day any window covers.
 */
export function firstCoveredDay<C extends CycleSpan & { phase?: string }>(
  w: DateWindow,
  cycles: readonly C[],
): { cycle: C; day: string } | null {
  const first = cyclesTouching(w, cycles)[0];
  if (!first) return null;
  return { cycle: first, day: first.horizon_start > w.from ? first.horizon_start : w.from };
}

/** "Mon 5 Oct – Sun 18 Oct · 14 days" (en-GB day-before-month, no year
 *  unless the two ends sit in different years). */
export function formatWindowLabel(w: DateWindow): string {
  const n = windowDays(w);
  return `${formatWindowSpan(w, true)} · ${n} day${n === 1 ? "" : "s"}`;
}

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * "29 Sep – 12 Oct" (or "Mon 29 Sep – Sun 12 Oct" with `withWeekday`) — the
 * roster page's header line. The year appears only when the ends sit in
 * different years.
 */
export function formatWindowSpan(w: DateWindow, withWeekday = false): string {
  const a = parseISO(w.from);
  const b = parseISO(w.to);
  const sameYear = a.getFullYear() === b.getFullYear();
  // Assembled from parts (not one `toLocaleDateString` call) so the label
  // reads "Mon 28 Dec 2026" — en-GB inserts a comma after the weekday when
  // a year is requested.
  const fmt = (d: Date, withYear: boolean) => {
    const weekday = withWeekday ? `${d.toLocaleDateString("en-GB", { weekday: "short" })} ` : "";
    // Fixed three-letter months: some ICU builds spell September "Sept".
    const month = MONTH_SHORT[d.getMonth()] ?? "";
    return `${weekday}${d.getDate()} ${month}${withYear ? ` ${d.getFullYear()}` : ""}`;
  };
  return w.from === w.to ? fmt(a, !sameYear) : `${fmt(a, !sameYear)} – ${fmt(b, !sameYear)}`;
}

export function todayIso(now: Date = new Date()): string {
  return toISO(now);
}
